import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useFinancialSummary, useExpenseBreakdown, useSalarySummary, useRecurringExpensesSummary } from '@/hooks/useAccounting'
import { TrendingUp, TrendingDown, DollarSign, ShoppingCart, Receipt, Users, Repeat } from 'lucide-react'
import type { ExpenseBreakdownItem } from '@/types/entities'

interface FinancialOverviewProps {
  restaurantId: string
  startDate: string
  endDate: string
  /** Can view salaries section (for assistants) */
  canViewSalaries?: boolean
  /** Can view expenses section (for assistants) */
  canViewExpenses?: boolean
}

export function FinancialOverview({
  restaurantId,
  startDate,
  endDate,
  canViewSalaries = true,
  canViewExpenses = true,
}: FinancialOverviewProps) {
  const { t, i18n } = useTranslation()
  const { data: summary, isLoading: summaryLoading } = useFinancialSummary(restaurantId, startDate, endDate)
  const { data: breakdown, isLoading: breakdownLoading } = useExpenseBreakdown(restaurantId, startDate, endDate)
  const { data: salarySummary } = useSalarySummary(restaurantId)
  const { data: recurringSummary } = useRecurringExpensesSummary(restaurantId)

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-MA' : 'fr-MA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  const isLoading = summaryLoading || breakdownLoading

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {[...Array(6)].map((_, i) => (
          <Card key={i} className="animate-pulse">
            <CardHeader className="pb-2">
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20" />
            </CardHeader>
            <CardContent>
              <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-16" />
            </CardContent>
          </Card>
        ))}
      </div>
    )
  }

  const cards = [
    {
      title: t('accounting.revenue'),
      value: summary?.totalRevenue || 0,
      icon: DollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100 dark:bg-blue-900/30',
      subtitle: `${summary?.totalOrders || 0} ${t('accounting.orders')}`,
    },
    {
      title: t('accounting.cogs'),
      value: -(summary?.totalCogs || 0),
      icon: ShoppingCart,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100 dark:bg-orange-900/30',
      subtitle: t('accounting.costOfGoodsSold'),
      negative: true,
    },
    {
      title: t('accounting.grossProfit'),
      value: summary?.grossProfit || 0,
      icon: summary && summary.grossProfit >= 0 ? TrendingUp : TrendingDown,
      color: summary && summary.grossProfit >= 0 ? 'text-green-600' : 'text-red-600',
      bgColor: summary && summary.grossProfit >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30',
      subtitle: t('accounting.revenueMinusCogs'),
    },
    {
      title: t('accounting.expenses'),
      value: -(summary?.totalExpenses || 0),
      icon: Receipt,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100 dark:bg-purple-900/30',
      subtitle: `${summary?.expenseCount || 0} ${t('accounting.entries')}`,
      negative: true,
    },
    {
      title: t('accounting.netProfit'),
      value: summary?.netProfit || 0,
      icon: summary && summary.netProfit >= 0 ? TrendingUp : TrendingDown,
      color: summary && summary.netProfit >= 0 ? 'text-green-600' : 'text-red-600',
      bgColor: summary && summary.netProfit >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30',
      subtitle: `${summary?.profitMargin || 0}% ${t('accounting.margin')}`,
    },
    {
      title: t('accounting.margin'),
      value: summary?.profitMargin || 0,
      icon: summary && summary.profitMargin >= 0 ? TrendingUp : TrendingDown,
      color: summary && summary.profitMargin >= 0 ? 'text-green-600' : 'text-red-600',
      bgColor: summary && summary.profitMargin >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30',
      subtitle: t('accounting.netProfitMargin'),
      isPercentage: true,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Main Financial Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {cards.map((card, index) => {
          const Icon = card.icon
          return (
            <Card key={index}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {card.title}
                </CardTitle>
                <div className={`p-2 rounded-full ${card.bgColor}`}>
                  <Icon className={`h-4 w-4 ${card.color}`} />
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold ${card.color}`}>
                  {card.isPercentage
                    ? `${card.value.toFixed(1)}%`
                    : `${card.negative ? '-' : ''}${formatCurrency(Math.abs(card.value))} ${t('common.currency')}`
                  }
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {card.subtitle}
                </p>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Monthly Commitments */}
      {(canViewSalaries || canViewExpenses) && (
        <div className="grid gap-4 md:grid-cols-2">
          {canViewSalaries && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('accounting.monthlySalaries')}</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(salarySummary?.totalMonthlySalary || 0)} {t('common.currency')}
                </div>
                <p className="text-xs text-muted-foreground">
                  {salarySummary?.activeEmployees || 0} {t('accounting.activeEmployees')}
                </p>
              </CardContent>
            </Card>
          )}

          {canViewExpenses && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{t('accounting.monthlyRecurring')}</CardTitle>
                <Repeat className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(recurringSummary?.totalMonthlyRecurring || 0)} {t('common.currency')}
                </div>
                <p className="text-xs text-muted-foreground">
                  {recurringSummary?.activeRecurringCount || 0} {t('accounting.activeRecurringExpenses')}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Expense Breakdown */}
      {breakdown && breakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('accounting.expenseBreakdown')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ExpenseBreakdownChart
              breakdown={breakdown}
              filterSalaries={!canViewSalaries}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

interface ExpenseBreakdownChartProps {
  breakdown: ExpenseBreakdownItem[]
  /** Filter out salaries category from the breakdown */
  filterSalaries?: boolean
}

function ExpenseBreakdownChart({ breakdown, filterSalaries = false }: ExpenseBreakdownChartProps) {
  const { t, i18n } = useTranslation()

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-MA' : 'fr-MA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  // Filter out salaries if needed (check both French and Arabic names)
  const filteredBreakdown = filterSalaries
    ? breakdown.filter(
        (item) =>
          item.categoryNameFr?.toLowerCase() !== 'salaires' &&
          item.categoryNameAr !== 'الرواتب'
      )
    : breakdown

  // Recalculate percentages after filtering
  const total = filteredBreakdown.reduce((sum, item) => sum + item.totalAmount, 0)
  const adjustedBreakdown = filteredBreakdown.map((item) => ({
    ...item,
    percentage: total > 0 ? Math.round((item.totalAmount / total) * 100) : 0,
  }))

  return (
    <div className="space-y-4">
      {/* Horizontal Bar Chart */}
      <div className="h-8 flex rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800">
        {adjustedBreakdown.map((item) => (
          <div
            key={item.categoryId}
            className="h-full transition-all duration-300"
            style={{
              width: `${item.percentage}%`,
              backgroundColor: item.categoryColor,
            }}
            title={`${i18n.language === 'ar' ? item.categoryNameAr : item.categoryNameFr}: ${item.percentage}%`}
          />
        ))}
      </div>

      {/* Legend */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {adjustedBreakdown.map((item) => (
          <div key={item.categoryId} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50">
            <div
              className="w-4 h-4 rounded-full flex-shrink-0"
              style={{ backgroundColor: item.categoryColor }}
            />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm truncate">
                {i18n.language === 'ar' ? item.categoryNameAr : item.categoryNameFr}
              </p>
              <p className="text-xs text-muted-foreground">
                {formatCurrency(item.totalAmount)} {t('common.currency')} ({item.percentage}%)
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="flex justify-between items-center pt-4 border-t">
        <span className="font-medium">{t('common.total')}</span>
        <span className="text-lg font-bold">
          {formatCurrency(total)} {t('common.currency')}
        </span>
      </div>
    </div>
  )
}
